<template>
  <div class="description-cell">
    <div class="description-download-wrap">
      <button-tip
        a
        icon
        iname="mdi-download"
        tip="Download"
        istyle="color:blue"
        btnstyle="margin: 0"
        :href="downloadHref"
        xsmall
      />
    </div>
    <div v-if="isEditing" class="description-edit-panel">
      <v-text-field
        ref="input"
        class="ml-4 mr-3 pr-3 py-0 file noUnderline description-input"
        style="margin-top: 2px"
        dense
        hide-details
        :error="!!errorMessage"
        :value="draft"
        @input="onInput"
        @blur="cancelEdit"
        @keydown.enter.prevent="saveEdit"
        @keydown.esc.prevent="cancelEdit"
      ></v-text-field>
      <div
        class="description-edit-message ml-4 mr-3 pr-3"
        :class="{ 'error--text': !!errorMessage }"
      >
        {{ errorMessage || 'Press Return to save' }}
      </div>
    </div>
    <button
      v-else-if="editable"
      type="button"
      class="description-readonly description-edit-trigger ml-4 mr-3 pr-3"
      @click="beginEdit"
    >
      {{ normalizedDescription }}
    </button>
    <span v-else class="description-readonly ml-4 mr-3 pr-3">
      {{ normalizedDescription }}
    </span>
  </div>
</template>

<script>
export default {
  name: "FirmFilesDescriptionCell",
  props: {
    description: {
      type: String,
      default: '',
    },
    downloadHref: {
      type: String,
      default: '',
    },
    editable: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      draft: this.description || '',
      errorMessage: '',
      isEditing: false,
    }
  },
  computed: {
    normalizedDescription() {
      return (this.description || '').toString();
    },
  },
  watch: {
    description(value) {
      if (!this.isEditing) {
        this.draft = (value || '').toString();
      }
    },
  },
  methods: {
    onInput(value) {
      this.draft = (value || '').toString();
      this.errorMessage = '';
    },

    getValidationMessage() {
      const firstFailure = this.rules
        .map(rule => rule(this.draft))
        .find(result => result !== true);
      return firstFailure === true || firstFailure === undefined ? '' : firstFailure;
    },

    focusInput() {
      this.$nextTick(() => {
        const inputComponent = this.$refs.input;
        if (!inputComponent) return;

        if (typeof inputComponent.focus === 'function') {
          inputComponent.focus();
        }

        const inputEl = inputComponent.$el && inputComponent.$el.querySelector('input');
        if (inputEl) {
          inputEl.focus();
          inputEl.select();
        }
      });
    },

    beginEdit() {
      if (!this.editable) return;

      this.isEditing = true;
      this.errorMessage = '';
      this.draft = this.normalizedDescription;
      this.focusInput();
    },

    cancelEdit() {
      if (!this.isEditing) return;

      this.isEditing = false;
      this.errorMessage = '';
      this.draft = this.normalizedDescription;
    },

    saveEdit() {
      if (!this.isEditing) return;

      const validationMessage = this.getValidationMessage();
      if (validationMessage) {
        this.errorMessage = validationMessage;
        this.focusInput();
        return;
      }

      this.$emit('save-description', this.draft);
      this.isEditing = false;
      this.errorMessage = '';
    },
  },
}
</script>

<style scoped>
.description-cell {
  align-items: flex-start;
  display: flex;
  min-width: 0;
  width: 100%;
}

.description-download-wrap {
  align-self: center;
  display: flex;
  flex: 0 0 auto;
  justify-content: center;
}

.description-edit-panel,
.description-readonly,
.description-edit-trigger {
  flex: 1 1 0%;
  max-width: 100%;
  min-width: 0;
}

.description-edit-panel {
  display: flex;
  flex-direction: column;
}

.description-input {
  flex: 0 0 auto;
  max-width: 100%;
  min-width: 0;
}

.description-edit-message {
  color: rgba(0, 0, 0, 0.82);
  font-size: 0.78rem;
  font-weight: 500;
  line-height: 1.25;
  margin-top: 4px;
}

.description-readonly,
.description-edit-trigger {
  line-height: 1.05;
  max-height: calc(2 * 1.05em + 2px);
  overflow: hidden;
  padding-top: 2px;
}

.description-readonly {
  line-clamp: 2;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  text-align: left;
  white-space: normal;
  word-break: break-word;
}

.description-edit-trigger {
  appearance: none;
  background: transparent;
  border: 0;
  color: inherit;
  cursor: text;
  font: inherit;
  margin: 0;
  padding-bottom: 0;
  padding-left: 0;
}
</style>
